import { type FullDateString } from "@@/shared/date-time/full-date";
import { priceZodSchema, type Price } from "@@/shared/prices/price";
import { z } from "zod";
import type { _ReportBase} from "../shared/_report-base";
import { _reportBaseZodSchema } from "../shared/_report-base";

type MonthlySalesColumn = {
    readonly name: string;
    readonly rows: Record<FullDateString, Price>;
    readonly correlationId: string;
    readonly columnValueType:
        | "AMOUNT_INCLUDING_VAT"
        | "AMOUNT_EXCLUDING_VAT"
        | "VAT_AMOUNT";
};
const monthlySalesColumnZodSchema: z.ZodSchema<MonthlySalesColumn> = z.object({
    name: z.string(),
    rows: z.record(z.number(), priceZodSchema),
    correlationId: z.string().min(1),
    columnValueType: z.enum([
        "AMOUNT_INCLUDING_VAT",
        "AMOUNT_EXCLUDING_VAT",
        "VAT_AMOUNT",
    ]),
});

type MonthlySales = _ReportBase & {
    readonly type: "MONTHLY_SALES";
    readonly month: number;
    readonly year: number;
    readonly columns: MonthlySalesColumn[];
    readonly totals: {
        columnName: string;
        value: Price;
        columnValueType:
            | "AMOUNT_INCLUDING_VAT"
            | "AMOUNT_EXCLUDING_VAT"
            | "VAT_AMOUNT";
    }[];
};

const monthlySalesZodSchema: z.ZodSchema<MonthlySales> = z.intersection(
    _reportBaseZodSchema,
    z.object({
        type: z.literal("MONTHLY_SALES"),
        month: z.number().min(1).max(12),
        year: z.number().min(1900).max(3000),
        columns: z.array(monthlySalesColumnZodSchema),
        totals: z.array(
            z.object({
                columnName: z.string().min(1),
                value: priceZodSchema,
                columnValueType: z.enum([
                    "AMOUNT_INCLUDING_VAT",
                    "AMOUNT_EXCLUDING_VAT",
                    "VAT_AMOUNT",
                ]),
            }),
        ),
    }),
);

export { monthlySalesColumnZodSchema, monthlySalesZodSchema };
export type { MonthlySales, MonthlySalesColumn };
